import React, { useState, useEffect, useLayoutEffect } from "react";
import Icon from "../../components/ui/icon";
import "../../assets/styles/pages/publishers/profile.css";
import SideNav from "../../components/layout/sideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import moment from "moment-timezone";
import DOMPurify from "dompurify";
import { getTimezoneByCountry } from "../../utils/getTimezoneByCountry";

const ProfilePage = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("PublisherInfo")) || {}
  );
  const country = userInfo?.country || "Unknown";
  const [logins, setLogins] = useState([]);
  const [isLoadingLogins, setIsLoadingLogins] = useState(true);
  const [upiID, setupiID] = useState("");
  const [hsBtnDisabled, sethsBtnDisabled] = useState(false);
  const [terminating, setTerminating] = useState(null);
  const [isLogginOut, setIsLogginOut] = useState(false);
  const [version, setVersion] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    if (userInfo?.payoutInfo?.upiID) {
      setupiID(userInfo?.payoutInfo?.upiID);
    }

    api
      .get(`/api/publisher/info`)
      .then((response) => {
        setUserInfo(response.data.data);
        localStorage.setItem(
          "PublisherInfo",
          JSON.stringify(response.data.data)
        );
        if (response.data?.data?.payoutInfo?.upiID) {
          setupiID(response.data?.data?.payoutInfo?.upiID);
        }
        api
          .get("/api/publisher/recent-logins")
          .then((response) => {
            setLogins(response.data.logins);
            setIsLoadingLogins(false);
          })
          .catch((err) => console.log("something went wrong"));
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Login not found.",
        });
        navigate("/auth/login");
      });
  }, [navigate, showToast, userInfo?.payoutInfo?.upiID]);

  const sanitizeInput = (value) => DOMPurify.sanitize(value);

  const handelUPIIDChange = (e) => {
    const value = sanitizeInput(e.target.value);
    setupiID(value);
  };

  const handelSaveBtn = () => {
    if (hsBtnDisabled) return;
    sethsBtnDisabled(true);

    const sanitizedUpi = sanitizeInput(upiID);
    if (!/^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(sanitizedUpi)) {
      document.getElementById("upiID").focus();
      sethsBtnDisabled(false);
      return;
    }

    api
      .put("/api/publisher/update-payout-info", { upiID: sanitizedUpi })
      .then((response) => {
        showToast({
          status: response.data.status ? "success" : "error",
          message:
            response.data.message || response.data.status
              ? "Updated Successfully."
              : "Something went wrong.",
        });
        sethsBtnDisabled(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        sethsBtnDisabled(false);
      });
  };

  const handelTerminateClick = (aToken) => {
    if (terminating !== null) return;
    const authToken = sanitizeInput(aToken);
    setTerminating(authToken);

    api
      .post("/api/publisher/terminate-session", { authToken })
      .then((response) => {
        const fixedMessage = response.data.status
          ? "Terminated Successfully."
          : "Something went wrong.";
        showToast({
          status: response.data.status ? "success" : "error",
          message: response.data.message || fixedMessage,
        });
        if (response.data.status) {
          setLogins((logins) =>
            logins.filter((l) => l.authToken !== authToken)
          );
        }
        setTerminating(null);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setTerminating(null);
      });
  };

  const handelLogout = () => {
    if (isLogginOut) return;
    setIsLogginOut(true);
    api
      .post("/api/publisher/logout")
      .then((response) => {
        localStorage.removeItem("aToken");
        localStorage.removeItem("PublisherInfo");
        showToast({
          status: response.data.status ? "success" : "error",
          message:
            response.data.message || response.data.status
              ? "Logged out successfully."
              : "Something went wrong.",
        });
        navigate("/auth/login");
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setIsLogginOut(false);
      });
  }

  useLayoutEffect(() => {
    const scriptTag = document.querySelector('script[src*="v="]');
    const linkTag = document.querySelector('link[href*="v="]');

    let v = null;

    if (scriptTag) {
      const src = new URL(scriptTag.src);
      v = src.searchParams.get("v");
    }

    if (!v && linkTag) {
      const href = new URL(linkTag.href);
      v = href.searchParams.get("v");
    }

    setVersion(v);
  }, []);

  return (
    <div className="profile__page">
      <SideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
        campaignOpenInital={false}
        accManagerName={userInfo.adminInfo?.name}
        accManagerEmail={userInfo.adminInfo?.email}
      />
      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>
      <main>
        <header className="desktop-header">
          <div>
            <h2>My Profile</h2>
            <p>Manage your profile easily from here.</p>
          </div>
        </header>

        <header className="mobile-header">
          <div>
            <h2>My Profile</h2>
          </div>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" className="onLeft" />
            ) : (
              <Icon name="Bars3" className="onLeft" />
            )}
          </button>
        </header>
        <div className="top-container">
          <div className="info-container">
            <div className="basic-infos">
              <div className="pimg-container">
                <Icon name="UserCircle" />
              </div>
              <div>
                <span>{userInfo.name || "Loading..."}</span>
                <span>{userInfo.role ? userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1).toLowerCase() : "Loading..."}</span>
              </div>

              <button className="logoutBtn" disabled={isLogginOut} onClick={handelLogout}>
                {isLogginOut ? (
                  <Icon name="ArrowPath" className="spin onRight" />
                ) : (
                  <Icon name="ArrowRightStartOnRectangle" />
                )}
              </button>
            </div>
            <div className="more-infos">
              <div className="more-info">
                <div className="info">
                  <span>Email ID:</span>
                  <span>{userInfo.email || "Loading..."}</span>
                </div>
              </div>
              <div className="more-info">
                <div className="info">
                  <span>Mobile Number:</span>
                  <span>
                    {`+91 ${userInfo.phone || "9999999999"}` || "Not Set"}
                  </span>
                </div>
              </div>
              <div className="more-info">
                <div className="info">
                  <span>Payment Method:</span>
                  <span>UPI</span>
                </div>
              </div>
              <div className="more-info">
                <div className="info">
                  <span>Country</span>
                  <span>{country}</span>
                </div>
              </div>
              <div className="more-info">
                <div className="info">
                  <span>Joined Swift Digital Ads On:</span>
                  <span>
                    {moment(userInfo.createdAt)
                      .tz(getTimezoneByCountry(country))
                      .format("DD MMM YYYY")}
                  </span>
                  <span className="version">v: {version}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="options-container">
            <div className="pInfo-container">
              <h2>Payment Information</h2>
              <div className="divider"></div>
              <div className="inp-box">
                <label>UPI ID</label>
                <input
                  value={upiID}
                  onChange={handelUPIIDChange}
                  type="text"
                  placeholder="username@bank"
                  id="upiID"
                />
              </div>
              <button disabled={hsBtnDisabled} onClick={handelSaveBtn}>
                {hsBtnDisabled ? (
                  <>
                    Saving
                    <Icon name="ArrowPath" className="spin onRight" />
                  </>
                ) : (
                  "Save"
                )}
              </button>
              <div className="alert-message">
                <Icon name="InformationCircle" />
                <p>
                  Withdrawal's you initiate will get into in payment account.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="logins-container">
          <h2>Recent Logins</h2>
          {isLoadingLogins && (
            <>
              <div className="login-infos loading"></div>
              <div className="login-infos loading"></div>
              <div className="login-infos loading"></div>
            </>
          )}
          {logins &&
            logins.length > 0 &&
            logins.map((login, index) => {
              return (
                <div className="login-infos" key={login.authToken}>
                  <div className="info">
                    <div className="icon-container">
                      {login.authenticationTimeDetails.user_agent ? (
                        /Mobile|Android|iPhone|iPad|iPod/i.test(
                          login.authenticationTimeDetails.user_agent
                        ) ? (
                          <Icon name="DevicePhoneMobile" />
                        ) : /Windows|Macintosh|Linux/i.test(
                          login.authenticationTimeDetails.user_agent
                        ) ? (
                          <Icon name="ComputerDesktop" />
                        ) : (
                          <Icon name="QuestionMarkCircle" />
                        )
                      ) : (
                        <Icon name="QuestionMarkCircle" />
                      )}
                    </div>
                    <div>
                      <span>
                        {login.authenticationTimeDetails.user_agent
                          ? /Mobile|Android|iPhone|iPad|iPod/i.test(
                            login.authenticationTimeDetails.user_agent
                          )
                            ? "Mobile"
                            : /Windows|Macintosh|Linux/i.test(
                              login.authenticationTimeDetails.user_agent
                            )
                              ? "Desktop"
                              : "Unknown"
                          : "Unknown"}{" "}
                        Login
                      </span>
                      <span>
                        {moment
                          .tz(
                            login.authenticationTimeDetails.time,
                            getTimezoneByCountry(country)
                          )
                          .format("DD MMM YYYY, h:mm A")}
                      </span>
                    </div>
                  </div>
                  <div className="action">
                    <button
                      onClick={() => handelTerminateClick(login.authToken)}
                    >
                      {login.authToken === terminating ? (
                        <Icon name="ArrowPath" className="spin" />
                      ) : (
                        "Terminate"
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        <span className="copyright-text">
          © 2025 Swift Digital Ads, All rights reserved
        </span>
      </main>
    </div>
  );
};

export default ProfilePage;
